import React from "react";
import { Snowflake, Heart, TreePine, Flame } from "lucide-react";
import { useTranslation } from "react-i18next";
import InteractiveCard from "./InteractiveCard";
import { useResponsive } from "../hooks/useResponsive";
import { colors } from "../styles/colors";
import LazyVideo from "./LazyVideo";
import { useContent } from "../cms";

const Features = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();

  const features = [
    {
      icon: <Heart className="h-8 w-8" style={{ color: colors.text.primary }} />,
      title: t('features.feature1Title'),
      description: t('features.feature1Desc'),
      iconBg: colors.glass.medium,
      iconColor: colors.text.primary,
      hasVideo: true,
      videoSrc: getMedia('features.feature1VideoUrl', '/optimized_videos/coldexperience1-opt.mp4'),
    },
    {
      icon: <Snowflake className="h-8 w-8" style={{ color: colors.text.primary }} />,
      title: t('features.feature2Title'),
      description: t('features.feature2Desc'),
      iconBg: colors.glass.dark,
      iconColor: colors.text.primary,
      hasVideo: true,
      videoSrc: getMedia('features.feature2VideoUrl', '/optimized_videos/coldexperience2-opt.mp4'),
    },
    {
      icon: <TreePine className="h-8 w-8" style={{ color: colors.text.primary }} />,
      title: t('features.feature3Title'),
      description: t('features.feature3Desc'),
      iconBg: colors.glass.medium,
      iconColor: colors.text.primary,
      hasVideo: true,
      videoSrc: getMedia('features.feature3VideoUrl', '/optimized_videos/coldexperience3-opt.mp4'),
    },
    {
      icon: <Flame className="h-8 w-8" style={{ color: colors.text.primary }} />,
      title: t('features.feature4Title'),
      description: t('features.feature4Desc'),
      iconBg: colors.glass.dark,
      iconColor: colors.text.primary,
      hasVideo: true,
      videoSrc: getMedia('features.feature4VideoUrl', '/nya_filmer/matlagning.mp4'),
    }
  ];

  return (
    <section data-cms-section="features" data-cms-page="home" className="py-20" style={{ background: colors.gradients.heroSolid }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4" style={{ color: colors.text.primary }}>
            {t('features.title')}
            <span style={{ color: colors.accent.primary }}> Cold Experience</span>
          </h2>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed px-4" style={{ color: colors.text.primary }}>
            {t('features.intro')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <InteractiveCard
              key={index}
              variant="tilt"
              className={`relative overflow-hidden rounded-2xl border border-slate-200 shadow-2xl hover:shadow-xl ${feature.hasVideo ? 'bg-transparent min-h-[300px] sm:min-h-[400px]' : 'bg-white'
                }`}
            >
              {/* Video Background for feature cards */}
              {feature.hasVideo && (
                <div className="absolute inset-0 z-0" style={{ backgroundColor: colors.primary }}>
                  <LazyVideo
                    src={feature.videoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload={isMobile ? "none" : "metadata"} // Mobile optimization
                    className="absolute inset-0"
                    videoClassName="absolute inset-0 w-full h-full object-cover"
                    pauseWhenOutOfView
                  />
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0" style={{ backgroundColor: `${colors.primary}99` }}></div>
                </div>
              )}

              <div className={`p-6 sm:p-8 relative z-10`} style={{ color: feature.hasVideo ? colors.text.primary : colors.primary }}>
                <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg mb-4 sm:mb-6`}
                  style={{
                    backgroundColor: feature.hasVideo ? colors.glass.medium : feature.iconBg,
                    border: feature.hasVideo ? `1px solid ${colors.border}` : 'none'
                  }}>
                  <div className={`w-5 h-5 sm:w-6 sm:h-6`} style={{ color: feature.hasVideo ? colors.text.primary : feature.iconColor }}>
                    {feature.icon}
                  </div>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4"
                  style={{ color: feature.hasVideo ? colors.text.primary : colors.primary }}>
                  {feature.title}
                </h3>

                <p className="text-sm sm:text-base leading-relaxed"
                  style={{ color: feature.hasVideo ? colors.text.primary : colors.text.primary }}>
                  {feature.description}
                </p>
              </div>
            </InteractiveCard>
          ))}
        </div>

        <div className="mt-12 sm:mt-16 text-center">
          <p className="text-base sm:text-lg max-w-4xl mx-auto leading-relaxed px-4" style={{ color: colors.text.primary }}>
            {t('features.outro')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Features;
